import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Form, Input, Typography } from 'antd'
import useUpdateLogo from 'pages/building/tourViewerPage/hook/Extra/useUpdateLogo'
import Validators from 'utils/validators'

import Button from 'components/atoms/Button'
import Modal from 'components/atoms/Modal'
import Spinner from 'components/atoms/Spinner'
import Title from 'components/atoms/Title'

type Props = {
  handleCancel: () => void
}
export default function ModalSettingLogo({ handleCancel }: Props) {
  const { t } = useTranslation()

  const {
    form,
    logoLink,
    thumbnail,
    inputRef,
    isFormValid,
    initialFormValue,
    handleSaveLogo,
    handleChooseFile,
    setIsLogoLinkHasErrors,
    handleFileChange,
    handleDeleteLogo,
    updating,
    deleting,
    isLoading
  } = useUpdateLogo({ handleCancel })

  // effect check logoLink errors
  useEffect(() => {
    const values = form.getFieldsValue()
    if (values?.logoLink) {
      form
        .validateFields(['logoLink'])
        .then(() => {
          setIsLogoLinkHasErrors(false)
        })
        .catch(() => {
          setIsLogoLinkHasErrors(true)
        })
    }
  }, [form, logoLink, setIsLogoLinkHasErrors])

  return (
    <Modal className="modal-custom" width={700} rootClassName="">
      <Title title={t('building.settingLogoTitle')} bottomMargin="mb-[31px]" />

      <Form
        initialValues={initialFormValue || undefined}
        form={form}
        colon={false}
        onFinish={handleSaveLogo}
        validateTrigger={['onChange', 'onBlur']}
      >
        <Spinner spinning={isLoading || updating || deleting}>
          <div>
            {/* Image */}
            <div className="flex justify-between items-center gap-x-4">
              <Typography.Text>{t('building.imageLabel')}</Typography.Text>

              {/* div show image selected  */}
              <Form.Item
                name="logoWrapper"
                dependencies={['logo']}
                hasFeedback
                className="logo-upload !mb-0"
              >
                <div onClick={handleChooseFile} className="ml-16">
                  {thumbnail ? (
                    <img
                      className="max-h-[118px] object-contain bg-[#021120]"
                      src={thumbnail}
                      alt="thumbnail"
                      loading="lazy"
                    />
                  ) : (
                    <Typography.Text>
                      {t('building.noImageNote')}
                    </Typography.Text>
                  )}
                </div>
              </Form.Item>
              <div className="flex flex-col gap-y-[2px] items-center mt-10">
                <div>
                  <div className="flex flex-col justify-center items-center">
                    <Typography
                      onClick={handleChooseFile}
                      className="bg-[#B3CFCB] mt-2 p-2 w-12 rounded-[10px] cursor-pointer hover:opacity-80"
                    >
                      {t('building.chooseLogoFileBtn')}
                    </Typography>
                    <div className="text-[10px] font-[400px] pt-2">
                      {t('building.fileSizeNote')}
                    </div>
                  </div>

                  <Form.Item
                    name="logo"
                    className="mb-0"
                    rules={[
                      {
                        validator: Validators.validateFileSize(t, 50)
                      }
                    ]}
                  >
                    <div className="hidden" />
                  </Form.Item>

                  {/* input file tách ra khỏi Form.Item */}
                  <input
                    accept=".jpg,.jpeg,.png,.JPG,.JPEG,.PNG"
                    type="file"
                    ref={inputRef}
                    onChange={handleFileChange}
                    hidden
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Link */}
          <div className="flex items-center gap-x-10">
            <Typography.Text className="text-sm font-bold">
              {t('building.urlLabel')}
            </Typography.Text>

            <Form.Item
              name="logoLink"
              className="thin-error-message mb-0"
              rules={[
                {
                  validator: Validators.urlValidator(t)
                }
              ]}
              validateFirst
            >
              <Input
                className="w-[300px] h-[38px] text-[14px] font-[400]"
                placeholder={t('common.buildingMenu.settingLogoPlaceHolder')}
              />
            </Form.Item>
          </div>
        </Spinner>
        {/* Action area */}
        <div className="flex gap-10 justify-center mt-10">
          {/* Button delete */}
          <Button
            onClick={handleDeleteLogo}
            sizing="w-[140px] h-[50px]"
            className="bg-[#B80808]"
          >
            {t('common.delete')}
          </Button>
          {/* Button cancel */}
          <Button sizing="w-[140px] h-[50px]" onClick={handleCancel}>
            {t('building.buttonCancel')}
          </Button>
          {/* Button save */}
          <Button
            htmlType="submit"
            type="primary"
            sizing="w-[140px] h-[50px]"
            disabled={!isFormValid}
          >
            {t('building.buttonSave')}
          </Button>
        </div>
      </Form>
    </Modal>
  )
}
